import { Cropper } from 'vue-advanced-cropper'
import { CropperInstance } from '@/types/cropper'
import { Vue, Component, Ref, Prop } from 'vue-property-decorator'
import cloneObject from '@/helpers/cloneObject'
import { SizeImage } from '@/types/image'
import { CropperImage } from '../types'

import ThumbnailCropperPreview from '../ui/ThumbnailCropperPreview/ThumbnailCropperPreview.vue'
import CropperContainer from '../ui/CropperContainer/CropperContainer.vue'
import SiriusAPI from '@/api'

@Component({
  components: {
    Cropper,
    CropperContainer,
    ThumbnailCropperPreview,
  },
})
export default class CropperMixin extends Vue {
  @Ref('cropper') protected readonly cropperRef?: CropperInstance
  @Prop({ type: Array, required: true })
  protected readonly cropperImages!: CropperImage[]
  @Prop({ type: Number, default: 0.8 })
  private readonly cropperAspectRatio!: number

  private cropperKey = Date.now()
  private cropperActiveImageIndex = 0
  protected localCropperImages: CropperImage[] = []

  private get cropperActiveImage() {
    return (
      this.localCropperImages[this.cropperActiveImageIndex] ||
      this.localCropperImages[0] ||
      this.localCropperImages.find((cropperImage) => !!cropperImage.aspectRatio)
    )
  }

  private get cropperStencilAspectRatio() {
    return this.cropperActiveImage?.aspectRatio
  }

  private get cropperStencilAspectRatioIsDinamic() {
    return !this.cropperStencilAspectRatio
  }

  private get cropperConfig() {
    return {
      transitions: false,
      transitionTime: 0,
      canvas: false,
      debounce: false,
      imageRestriction: this.cropperStencilAspectRatioIsDinamic
        ? 'fit-area'
        : 'stencil',
      stencilProps: {
        lines: {},
        handlers: {},
        movable: false,
        scalable: false,
        aspectRatio: this.cropperStencilAspectRatio,
      },
      resizeImage: {
        wheel: {
          ratio: 0.04,
        },
      },
    }
  }

  private get cropperOriginalImageUrl() {
    return this.cropperActiveImage?.transform.image?.url
    // return https://images.thevoicemag.ru/upload/img_cache/9ff/9ff3ebd1a1997358fac3ccf8e9d40745_cropped_112x200.webp
    // return 'https://www.pravilamag.ru/upload/img_cache/f18/f1861ef14bd9f7a9c615ac3379f6f06c_ce_2336x2336x584x0_cropped_1240x1240.webp'
  }

  protected get usedOneImageForAllCropperImages() {
    const imageId = this.cropperActiveImage?.transform.image_id
    const imageCid = this.cropperActiveImage?.transform.image?.cid

    return this.localCropperImages.every((cropperImage) => {
      if (imageCid) {
        return cropperImage.transform.image?.cid === imageCid
      }

      return cropperImage.transform.image_id === imageId
    })
  }

  private getCropperDefaultPosition({
    imageSize,
  }: {
    imageSize: {
      width: number
      height: number
    }
  }) {
    if (
      !this.cropperActiveImage?.transform.crop_h ||
      !this.cropperActiveImage?.transform.crop_w
    ) {
      const { top, left } = this.calcCropperCoordinates({
        visibleArea: {
          top: 0,
          left: 0,
          width: imageSize.width,
          height: imageSize.height,
        },
        stencilAspectRatio: this.cropperStencilAspectRatio,
        imageSize,
      })

      return {
        top,
        left,
      }
    }

    if (this.cropperStencilAspectRatioIsDinamic && this.cropperRef) {
      const { boundaries } = this.cropperRef
      const cropX = this.cropperActiveImage.transform.crop_x
      const cropY = this.cropperActiveImage.transform.crop_y

      const visibleArea = {
        width: 0,
        height: 0,
      }

      let cropWidth = 0
      let cropHeight = 0
      let left = this.cropperActiveImage.transform.crop_x
      let top = this.cropperActiveImage.transform.crop_y

      if (imageSize.width > imageSize.height) {
        cropHeight =
          this.cropperActiveImage.transform.crop_w *
          (imageSize.height / imageSize.width)
        visibleArea.width = this.cropperActiveImage.transform.crop_w
        visibleArea.height =
          visibleArea.width * (boundaries.height / boundaries.width)

        top = Math.round(
          cropY > 0
            ? (visibleArea.height - cropHeight) / 2 + cropY
            : (imageSize.height - cropHeight) / 2
        )
      } else {
        cropWidth =
          this.cropperActiveImage.transform.crop_h *
          (imageSize.width / imageSize.height)
        visibleArea.height = this.cropperActiveImage.transform.crop_h
        visibleArea.width =
          visibleArea.height * (boundaries.width / boundaries.height)

        left = Math.round(
          cropX > 0
            ? (visibleArea.width - cropWidth) / 2 + cropX
            : (imageSize.width - cropWidth) / 2
        )
      }

      return {
        left: Math.max(left, 0),
        top: Math.max(top, 0),
      }
    }

    return {
      top: this.cropperActiveImage.transform.crop_y,
      left: this.cropperActiveImage.transform.crop_x,
    }
  }

  private getCropperDefaultSize({
    imageSize,
  }: {
    imageSize: {
      width: number
      height: number
    }
  }) {
    if (this.cropperStencilAspectRatioIsDinamic && this.cropperRef) {
      let width = imageSize.width
      let height = imageSize.height

      if (imageSize.width > imageSize.height) {
        width = this.cropperActiveImage.transform.crop_w || 0
        height =
          this.cropperActiveImage.transform.crop_w *
          (imageSize.width / imageSize.height)
      } else {
        height = this.cropperActiveImage.transform.crop_h || 0
        width =
          this.cropperActiveImage.transform.crop_h *
          (imageSize.height / imageSize.width)
      }

      width = Math.round(Math.min(width, imageSize.width))
      height = Math.round(Math.min(height, imageSize.height))

      return {
        width,
        height,
      }
    }

    return {
      width: this.cropperActiveImage.transform.crop_w || imageSize.width,
      height: this.cropperActiveImage.transform.crop_h || imageSize.height,
    }
  }

  private getCropperStencilSize({
    boundaries,
  }: {
    boundaries: {
      width: number
      height: number
    }
  }) {
    if (this.cropperStencilAspectRatioIsDinamic && this.cropperRef) {
      const {
        imageSize: { width: imageWidth, height: imageHeight },
      } = this.cropperRef
      const aspectRatio = imageWidth / imageHeight
      const width = Math.round(boundaries.height * aspectRatio)

      return {
        width,
        height: boundaries.height,
      }
    }

    return {
      width: boundaries.width,
      height: boundaries.height,
    }
  }

  private getCropperDefaultBoundaries({
    cropper,
  }: {
    cropper: HTMLDivElement
  }) {
    return {
      width: cropper.clientWidth,
      height: cropper.clientHeight,
    }
  }

  private calcCropperCoordinatesFactor({
    coordinates,
    imageSize,
  }: {
    imageSize: {
      width: number
      height: number
    }
    coordinates: {
      width: number
      height: number
      top: number
      left: number
    }
  }) {
    const factorLeft =
      coordinates.left / (imageSize.width - coordinates.width) || 0
    const factorTop =
      coordinates.top / (imageSize.height - coordinates.height) || 0

    return {
      factorLeft,
      factorTop,
    }
  }

  private calcCropperCoordinates({
    factorLeft = 0.5,
    factorTop = 0.5,
    stencilAspectRatio,
    imageSize,
    visibleArea,
  }: {
    factorLeft?: number
    factorTop?: number
    stencilAspectRatio?: number
    imageSize: {
      width: number
      height: number
    }
    visibleArea: {
      width: number
      height: number
      left: number
      top: number
    }
  }) {
    let width = visibleArea.width
    let height = visibleArea.height
    let left = 0
    let top = 0

    if (stencilAspectRatio) {
      width = Math.round(
        Math.min(visibleArea.height * stencilAspectRatio, visibleArea.width)
      )
      height = Math.round(
        Math.min(visibleArea.width / stencilAspectRatio, visibleArea.height)
      )
      left = Math.round((imageSize.width - width) * factorLeft)
      top = Math.round((imageSize.height - height) * factorTop)
    } else {
      width = Math.round(Math.min(visibleArea.width, imageSize.width))
      height = Math.round(Math.min(visibleArea.height, imageSize.height))
      left = visibleArea.left > 0 ? Math.round(visibleArea.left) : 0
      top = visibleArea.top > 0 ? Math.round(visibleArea.top) : 0
    }

    top = top || 0
    left = left || 0
    height = height || 0
    width = width || 0

    return {
      left,
      top,
      width,
      height,
    }
  }

  private onChangeCropper({
    coordinates,
    image: imageSize,
    visibleArea,
  }: {
    coordinates: {
      width: number
      height: number
      left: number
      top: number
    }
    image: {
      width: number
      height: number
    }
    visibleArea: {
      width: number
      height: number
      left: number
      top: number
    }
  }) {
    if (this.cropperActiveImageIndex > -1) {
      this.cropperActiveImage.transform.crop_h = Math.round(
        this.cropperStencilAspectRatioIsDinamic
          ? Math.min(visibleArea.height, imageSize.height)
          : coordinates.height
      )

      this.cropperActiveImage.transform.crop_w = Math.round(
        this.cropperStencilAspectRatioIsDinamic
          ? Math.min(visibleArea.width, imageSize.width)
          : coordinates.width
      )

      this.cropperActiveImage.transform.crop_x = Math.round(
        this.cropperStencilAspectRatioIsDinamic
          ? Math.max(visibleArea.left, 0)
          : coordinates.left
      )

      this.cropperActiveImage.transform.crop_y = Math.round(
        this.cropperStencilAspectRatioIsDinamic
          ? Math.max(visibleArea.top, 0)
          : coordinates.top
      )
    } else {
      const { factorLeft, factorTop } = this.calcCropperCoordinatesFactor({
        coordinates,
        imageSize,
      })

      const { left, top, width, height } = this.calcCropperCoordinates({
        factorLeft,
        factorTop,
        visibleArea,
        stencilAspectRatio: this.cropperStencilAspectRatio,
        imageSize,
      })

      this.localCropperImages.forEach((cropperImage) => {
        cropperImage.transform.crop_x = left
        cropperImage.transform.crop_y = top
        cropperImage.transform.crop_w = width
        cropperImage.transform.crop_h = height
      })
    }

    const imageForAllFormats = this.cropperActiveImage.transform.image

    this.localCropperImages.forEach((cropperImage) => {
      const image =
        this.usedOneImageForAllCropperImages && imageForAllFormats
          ? imageForAllFormats
          : cropperImage.transform.image

      if (!image) return

      cropperImage.transform.image = { ...image }
    })
  }

  protected onCropperImageUploader({
    url,
    id,
    width,
    height,
    file,
    cid,
    copyrightImage,
  }: SizeImage & { cid?: string }) {
    if (this.usedOneImageForAllCropperImages) {
      this.localCropperImages.forEach((cropperImage) => {
        const coordinates = this.calcCropperCoordinates({
          visibleArea: {
            top: 0,
            left: 0,
            width: width,
            height: height,
          },
          stencilAspectRatio: cropperImage.aspectRatio,
          imageSize: {
            width,
            height,
          },
        })

        cropperImage.transform.crop_h = coordinates.height
        cropperImage.transform.crop_w = coordinates.width
        cropperImage.transform.crop_x = coordinates.left
        cropperImage.transform.crop_y = coordinates.top
        cropperImage.transform.image_id = id
        cropperImage.transform.image = {
          url,
          id,
          file,
          cid,
          copyrightImage,
        }
      })
    } else {
      const coordinates = this.calcCropperCoordinates({
        visibleArea: {
          top: 0,
          left: 0,
          width: width,
          height: height,
        },
        stencilAspectRatio: this.cropperActiveImage.aspectRatio,
        imageSize: {
          width,
          height,
        },
      })

      this.cropperActiveImage.transform.crop_h = coordinates.height
      this.cropperActiveImage.transform.crop_w = coordinates.width
      this.cropperActiveImage.transform.crop_x = coordinates.left
      this.cropperActiveImage.transform.crop_y = coordinates.top
      this.cropperActiveImage.transform.image_id = id

      this.cropperActiveImage.transform.image = {
        url,
        id,
        cid,
        file,
        copyrightImage,
      }
    }
  }

  protected onCropperReplaceImage(index: number) {
    const cropperImage = this.localCropperImages[index]

    cropperImage.transform.image = {
      url: '',
      id: 0,
      copyrightImage: {
        url: '',
        source: '',
        source_url: '',
      },
    }
    cropperImage.transform.image_id = null

    this.setCropperActiveImage(index)
  }

  private setCropperActiveImage(index: number) {
    this.cropperActiveImageIndex = index
    // this.cropperActiveImageIndex =
    //   index === this.cropperActiveImageIndex ? -1 : index
  }

  private async loadImages() {
    const imagesMap = new Map<string, number>()
    const unique = new Map<string, { id: number; cropperImage: CropperImage }>()

    this.localCropperImages.forEach((cropperImage) => {
      const image = cropperImage.transform.image

      if (image?.id) {
        if (!unique.has(image.id.toString())) {
          unique.set(image.id.toString(), {
            id: image.id,
            cropperImage,
          })
        }
      } else if (image?.file && image?.cid) {
        if (!unique.has(image.cid)) {
          unique.set(image.cid, {
            id: 0,
            cropperImage,
          })
        }
      }
    })

    await Promise.all(
      Array.from(unique.entries()).map(async ([cid, { id, cropperImage }]) => {
        if (!id) {
          const newImage = await this.createImage(cropperImage)

          if (newImage) {
            imagesMap.set(cid, newImage.id)
          }
        } else if (cropperImage?.transform?.image) {
          const image = cropperImage.transform.image

          await SiriusAPI.image.updateImageSource({
            id,
            source: image?.copyrightImage?.source || '',
            source_url: image?.copyrightImage?.source_url || '',
            copyright: image?.copyrightImage?.copyright_file || undefined,
          })
        }
      })
    )

    return imagesMap
  }

  private async createImage(cropperImage: CropperImage) {
    const image = cropperImage.transform.image

    if (!image || !image.copyrightImage?.source || !image.file) return null

    const formData = new FormData()

    formData.append('file', image.file)
    formData.append('source', image.copyrightImage.source)

    if (image.copyrightImage?.copyright_file) {
      formData.append('copyright', image.copyrightImage.copyright_file)
      formData.append('source_url', image.copyrightImage.source_url)
    }

    const { item } = await SiriusAPI.image.addImage({ file: formData })

    return item
  }

  private async handleSaveCropperImages() {
    let isValid = true

    this.localCropperImages.forEach((cropperImage) => {
      if (!isValid) return

      if (
        !cropperImage.transform.image_id &&
        cropperImage.transform.image?.file &&
        !cropperImage.transform.image?.copyrightImage?.source
      ) {
        isValid = false
      }
    })

    if (!isValid) {
      this.$notify({
        type: 'error',
        title: 'Обязательные поля не заполнены',
      })
      return
    }

    const uploadedImages = await this.loadImages()

    const promises = this.localCropperImages.map((cropperImage) => {
      return new Promise((resolve) => {
        const saveImages = async () => {
          try {
            if (
              !cropperImage.transform.image_id &&
              cropperImage.transform.image?.cid
            ) {
              const imageId = uploadedImages.get(
                cropperImage.transform.image.cid
              )
              if (imageId) {
                cropperImage.transform.image_id = imageId
              }
            }

            if (cropperImage.transform.image_id) {
              if (!cropperImage.transform.id) {
                const {
                  item: { id },
                } = await SiriusAPI.image.addImageTransform({
                  crop_h: cropperImage.transform.crop_h,
                  crop_w: cropperImage.transform.crop_w,
                  crop_x: cropperImage.transform.crop_x,
                  crop_y: cropperImage.transform.crop_y,
                  image_id: cropperImage.transform.image_id,
                })

                cropperImage.transform.id = id
              } else {
                await SiriusAPI.image.changeImageTransform({
                  id: cropperImage.transform.id,
                  crop_h: cropperImage.transform.crop_h,
                  crop_w: cropperImage.transform.crop_w,
                  crop_x: cropperImage.transform.crop_x,
                  crop_y: cropperImage.transform.crop_y,
                  image_id: cropperImage.transform.image_id,
                  responseStatusOnly: 1,
                })
              }
            }
          } finally {
            resolve(null)
          }
        }

        saveImages()
      })
    })

    await Promise.all(promises)

    return this.localCropperImages
  }

  protected refreshCropper(): void {
    this.cropperRef?.refresh()
  }

  protected initCropper(): void {
    const emptyImageIndex = this.cropperImages.findIndex(
      ({ transform: { id: imageTransformId } }) => !imageTransformId
    )
    this.setCropperActiveImage(Math.max(emptyImageIndex, 0))
    this.localCropperImages = cloneObject(this.cropperImages)
    this.cropperKey++
  }
}
